/**
 * Install-discovery helpers. The browsers that support
 * `beforeinstallprompt` (Chrome/Edge/Android Chrome/Samsung Internet)
 * fire it once per page load before any user gesture; we capture the
 * event so a later button click can present the prompt. iOS Safari and
 * macOS Safari don't support it — the InstallModal falls back to text
 * instructions for those platforms.
 */

import { TRIP_PATH_REGEX } from '~/shared/token';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function initPwa(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
}

export function canShowNativePrompt(): boolean {
  return deferredPrompt !== null;
}

export async function showNativePrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  const prompt = deferredPrompt;
  deferredPrompt = null;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  return choice.outcome;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

const LAST_PATH_KEY = 'lazy-traveler:last-path';
const LAUNCHED_KEY = 'lazy-traveler:launched';

/**
 * Home-screen launches always open the manifest's start_url ("/"),
 * which the server bounces to the signed-in dashboard — so an iOS
 * standalone app evicted in the background reopens on My trips
 * instead of the trip the user was on. Browser tabs restore their own
 * URL, so this only applies in standalone mode.
 *
 * Every page load records its path. On a cold launch (no session
 * marker yet) that landed off a trip page, returns the last trip path
 * to resume. Later loads in the same session are deliberate
 * navigation and are left alone. Storage failures (private mode,
 * blocked site data) degrade to the plain start_url behaviour.
 */
export function resumePathOnLaunch(): string | null {
  if (!isStandalone()) return null;
  const current = window.location.pathname;
  try {
    const coldLaunch = sessionStorage.getItem(LAUNCHED_KEY) === null;
    sessionStorage.setItem(LAUNCHED_KEY, '1');
    const last = localStorage.getItem(LAST_PATH_KEY);
    if (
      coldLaunch &&
      last !== null &&
      last !== current &&
      TRIP_PATH_REGEX.test(last) &&
      !TRIP_PATH_REGEX.test(current)
    ) {
      return last;
    }
    localStorage.setItem(LAST_PATH_KEY, current);
  } catch {
    // Storage unavailable — fall through to start_url.
  }
  return null;
}

export type InstallPlatform =
  | 'ios'
  | 'macos-safari'
  | 'android'
  | 'desktop-chromium'
  | 'desktop-firefox'
  | 'unknown';

export function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  // iPadOS reports a desktop UA; the touch-points check disambiguates.
  if (/iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  if (/Android/.test(ua)) return 'android';
  if (/Mac/.test(ua) && /Safari/.test(ua) && !/Chrome|Edg|Chromium/.test(ua)) return 'macos-safari';
  if (/Firefox/.test(ua)) return 'desktop-firefox';
  return 'desktop-chromium';
}
